/**
 * StairsTypeModal — V14.62 Sprint 21 (Lab).
 *
 * Petite modale qui s'ouvre après le 2ᵉ clic de l'outil Escalier. L'utilisateur
 * choisit la typologie (Droit / Tournant / Colimaçon). Par défaut "Droit".
 * Esc = annule, Entrée = valide la sélection courante.
 */
import { useEffect, useMemo, useState } from 'react';
import { StretchHorizontal, CornerDownRight, Tornado, X } from 'lucide-react';
import { useGeometryStore, STAIRS_KINDS } from './useGeometryStore';

const KINDS = [
  { value: STAIRS_KINDS.STRAIGHT, label: 'Droit',     icon: StretchHorizontal, hint: '1 volée linéaire' },
  { value: STAIRS_KINDS.TURNING,  label: 'Tournant',  icon: CornerDownRight,   hint: 'Quart ou demi-tour' },
  { value: STAIRS_KINDS.SPIRAL,   label: 'Colimaçon', icon: Tornado,           hint: 'Hélicoïdal autour d\'un axe' },
];

const StairsTypeModal = () => {
  const pending = useGeometryStore((s) => s.stairsPending);
  const scale = useGeometryStore((s) => s.scale_m_per_px);
  const commitStairs = useGeometryStore((s) => s.commitStairs);
  const cancelStairs = useGeometryStore((s) => s.cancelStairs);
  const [selected, setSelected] = useState(STAIRS_KINDS.STRAIGHT);

  useEffect(() => {
    if (pending) setSelected(STAIRS_KINDS.STRAIGHT);
  }, [pending]);

  // Compute real-world surface from the rectangle + active scale.
  const surfaceM2 = useMemo(() => {
    if (!pending || scale <= 0) return null;
    const wM = pending.w * scale;
    const hM = pending.h * scale;
    return wM * hM;
  }, [pending, scale]);

  useEffect(() => {
    if (!pending) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cancelStairs(); }
      if (e.key === 'Enter')  { e.preventDefault(); commitStairs(selected); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, selected, commitStairs, cancelStairs]);

  if (!pending) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-sm"
      style={{ background: 'rgba(2,6,12,0.7)' }}
      data-testid="stairs-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) cancelStairs(); }}
    >
      <div
        className="w-[480px] max-w-[92vw] rounded-2xl border overflow-hidden shadow-2xl"
        style={{ background: 'rgba(8,12,20,0.96)', borderColor: 'rgba(168,85,247,0.4)' }}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b"
             style={{ borderColor: 'rgba(168,85,247,0.25)' }}>
          <StretchHorizontal className="w-4 h-4 text-purple-400" />
          <h3 className="flex-1 text-sm font-semibold text-white">
            Type d&apos;escalier / trémie
          </h3>
          <button type="button" onClick={cancelStairs}
                  className="w-7 h-7 inline-flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-white/5"
                  data-testid="stairs-modal-close"
                  title="Annuler (Échap)">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-4">
          <p className="text-[12px] text-gray-300 leading-relaxed">
            Zone tracée :{' '}
            <strong className="text-purple-300 font-mono">
              {pending.w.toFixed(0)} × {pending.h.toFixed(0)} px
            </strong>
            {surfaceM2 !== null && (
              <> · <strong className="text-emerald-300 font-mono">{surfaceM2.toFixed(2)} m²</strong></>
            )}
            . Sélectionnez la typologie pour informer le moteur 3D
            (extrusion verticale + trémie de plancher).
          </p>

          <div className="grid grid-cols-3 gap-2" role="radiogroup">
            {KINDS.map(({ value, label, icon: Icon, hint }) => {
              const active = selected === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSelected(value)}
                  data-testid={`stairs-kind-${value}`}
                  aria-pressed={active}
                  className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${
                    active
                      ? 'bg-purple-500/20 border-purple-400 text-purple-100'
                      : 'bg-black/30 border-white/10 text-gray-300 hover:bg-white/5 hover:border-purple-400/40'
                  }`}
                >
                  <Icon className={`w-6 h-6 ${active ? 'text-purple-300' : 'text-gray-400'}`} />
                  <span className="text-[11.5px] font-medium">{label}</span>
                  <span className="text-[9.5px] text-gray-500 text-center leading-tight">{hint}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-t bg-black/30"
             style={{ borderColor: 'rgba(168,85,247,0.18)' }}>
          <button type="button" onClick={cancelStairs}
                  className="px-3 py-1.5 text-[11px] rounded border border-white/15 text-gray-300 hover:bg-white/5"
                  data-testid="stairs-modal-cancel">
            Annuler
          </button>
          <div className="flex-1" />
          <button type="button"
                  onClick={() => commitStairs(selected)}
                  className="px-4 py-1.5 text-[11px] rounded bg-purple-500 hover:bg-purple-400 text-purple-50 font-semibold"
                  data-testid="stairs-modal-submit">
            Placer l&apos;escalier ↵
          </button>
        </div>
      </div>
    </div>
  );
};

export default StairsTypeModal;
